import { type Theme } from './theme.server.ts'

/**
 * THE THEME SWITCH — one constant, three consumers, one line to reverse.
 *
 * `FORCED_THEME` pins every surface to a single theme and hides every control
 * that would change it. Set it back to `null` and the app returns to honouring
 * the `en_theme` cookie (falling back to the OS client hint), with the switches
 * visible again. Nothing else has to change: no route is deleted, the cookie and
 * `/resources/theme-switch` action still work, and the `.dark` token block in
 * styles/tailwind.css stays where it is.
 *
 * It is deliberately a constant rather than a setting. The decision is a
 * product one that has already flipped twice (issue 102); a value in the code
 * reads in a diff and travels with the branch, where a DB row would not.
 *
 * Consumers — all three must stay in step, which is what `THEME_SWITCHER`
 * exists to guarantee:
 *   1. root.tsx `Layout`  — the `<html>` class, which every token and every
 *      `dark:` utility keys off. This is the one that actually locks it.
 *   2. root.tsx `App`     — the toast, which picks its own palette.
 *   3. components/site-footer.tsx + components/nav-user.tsx — the controls.
 *
 * Note the literal class matters, not just the absence of `dark`:
 * `utils/clinic-hours-viz.ts` carries an `@media (prefers-color-scheme: dark)`
 * block guarded by `:root:not(.light)`. A media query fires on OS preference
 * whatever the class says, so the charts stay light only because `light` is
 * emitted explicitly. Never "simplify" that to an empty class.
 */
export const FORCED_THEME: Theme | null = 'light'

/**
 * Whether the user may change the theme. Derived, never set by hand — a forced
 * theme with a live switch is a control that appears to work and doesn't.
 */
export const THEME_SWITCHER = FORCED_THEME === null
